<template>
  <div
    class="flex w-full justify-between items-center px-4 p-3 border border-x-0 border-t-0 border-b-1 border-prime-golden bg-pitch-black-900"
  >
    <!-- Logo -->
    <a href="/" class="text-prime-golden text-base font-avenir-medium"
      ><img
        class="w-24 h-24 -my-8 -ml-[10px]"
        src="@/assets/images/mason-logo.svg"
        alt="Mason Logo"
    /></a>

    <!-- User Drawer Trigger -->
    <button @click="toggleDrawer" class="text-prime-golden text-xs w-4 h-4">
      <img src="@/assets/images/menu.svg" alt="Menu" />
    </button>

    <!-- User Drawer Component -->
    <UserDrawer
      :isOpen="drawerOpen"
      :first_name="user.first_name"
      :last_name="user.last_name"
      :email="user.email"
      :projectId="projectId"
      :isSimulation="isSimulation"
      :projectName="projectName"
      :isThread="isThread"
      :userRole="userRole"
      :userPicture="user.profile_image_url"
      @close="toggleDrawer"
      @logout="handleLogout"
      @openResetModal="openResetModal"
    />
    <ResetModal
      v-if="isResetModalOpen"
      :isOpen="isResetModalOpen"
      @confirmReset="resetProject"
      @cancelReset="closeResetModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import UserDrawer from "@/components/UserDrawer.vue";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import ResetModal from "./ResetModal.vue";
import { useActivationStore } from "@/stores/activation";
import { useToast } from "vue-toastification";

const drawerOpen = ref(false);
const userStore = useUserStore();
const authStore = useAuthStore();
const activationStore = useActivationStore();
const toast = useToast();

const router = useRouter();
const isResetModalOpen = ref(false);
const props = defineProps({
  isThread: { type: Boolean, required: true },
  userRole: { type: String, required: false },
  isSimulation: { type: Boolean, default: false },
  projectId: { type: [String, Number], required: false },
  projectName: { type: String, required: false },
});

const resetProject = async () => {
  try {
    // Fetch the activation details
    const activationDetails = await activationStore.getActivation(props.projectId);

    if (!activationDetails) {
      toast.error("Failed to fetch project activation details.");
      return;
    }

    // Reset the project using the fetched activation details
    await activationStore.resetActivation({
      project_id: activationDetails.project?.id,
      agent_id: activationDetails.activation?.agent_stakeholder?.id,
      is_active: activationDetails.activation?.is_active,
      instruction_id: activationDetails.activation?.instruction?.id,
      agent_email_id: activationDetails.activation?.agent_email_id,
      agent_phone: activationDetails.activation?.agent_phone,
    });

    toast.success("Project reset successfully.");
    closeResetModal();
  } catch (error) {
    toast.error("Failed to reset project. Please try again.");
    closeResetModal();
    console.error("Error resetting project:", error);
  }
};
const closeResetModal = () => {
  isResetModalOpen.value = false;
};
const openResetModal = () => {
  isResetModalOpen.value = true;
  toggleDrawer();
};
// Fetch user profile on component mount if not available in the store
onMounted(async () => {
  if (!userStore.profile) {
    try {
      await userStore.fetchUserProfile();
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  }
});

// Computed property for user data
const user = computed(() => {
  return (
    userStore.profile || {
      first_name: "...",
      last_name: "...",
      email: "",
    }
  );
});

// Toggle the user drawer open/close state
const toggleDrawer = () => {
  drawerOpen.value = !drawerOpen.value;
};

// Handle user logout
const handleLogout = () => {
  authStore.logout();
  router.push({ name: "LogIn" });
  toggleDrawer(); // Close drawer after logging out
};
</script>

<style scoped>
/* Add any necessary navbar-specific styles here */
</style>
